import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import { SITE_NAME, SITE_URL, usePageSeo } from "@/hooks/usePageSeo";
import { works } from "@/mocks/works";
import { formatDate, formatNumber } from "@/utils/format";

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => w.id === id);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!work) return [];
    const sameCategory = works.filter((w) => w.id !== work.id && w.category === work.category);
    const others = works.filter((w) => w.id !== work.id && w.category !== work.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [work]);

  usePageSeo({
    title: work ? `${work.title} - 爆料大赛参赛作品` : "作品未找到 - 爆料大赛",
    description: work
      ? `${work.summary}（${work.categoryName}·${work.author}）`
      : "该参赛作品不存在或已下线，返回作品集浏览其他投稿作品。",
    keywords: work ? `爆料大赛,${work.categoryName},${work.tags.join(",")},参赛作品` : "爆料大赛参赛作品",
    path: work ? `/work/${work.id}` : "/works",
    type: "article",
    jsonLd: work
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: work.title,
            description: work.summary,
            datePublished: work.date,
            inLanguage: "zh-CN",
            articleSection: work.categoryName,
            keywords: work.tags.join(","),
            author: { "@type": "Person", name: work.author },
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            mainEntityOfPage: `${SITE_URL}/work/${work.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "参赛作品", item: `${SITE_URL}/works` },
              { "@type": "ListItem", position: 3, name: work.title, item: `${SITE_URL}/work/${work.id}` },
            ],
          },
        ]
      : undefined,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!work) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="max-w-md text-center">
            <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
              <i className="ri-file-unknow-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇作品还没有上线</h1>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              可能链接已经调整，或者该投稿还在审核中。回到作品集看看其他参赛作品吧。
            </p>
            <Link
              to="/works"
              className="mt-6 inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-5 py-3 rounded-md whitespace-nowrap transition-colors cursor-pointer"
            >
              返回参赛作品集
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-background-100 border-b border-background-200 px-4 md:px-8 py-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "参赛作品", to: "/works" },
              { label: work.categoryName, to: `/works?category=${work.category}` },
              { label: work.title },
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug tracking-tight max-w-4xl">
            {work.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 flex-wrap text-xs md:text-sm text-foreground-600">
            <span className="flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
                <i className="ri-user-3-line text-sm" />
              </span>
              {work.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line" />
              </span>
              {formatDate(work.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line" />
              </span>
              阅读约 {work.readTime}
            </span>
            <Link
              to={`/works?category=${work.category}`}
              className="flex items-center gap-1.5 text-primary-700 bg-primary-100 px-3 py-1 rounded-full cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-price-tag-3-line" />
              </span>
              {work.categoryName}
            </Link>
          </div>
        </section>

        <section className="px-4 md:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8">
              <div className="w-full h-[240px] md:h-[420px] rounded-lg overflow-hidden bg-background-200">
                <img src={work.cover} alt={work.title} className="w-full h-full object-cover object-top" />
              </div>

              <p className="mt-7 text-base md:text-lg text-foreground-800 leading-relaxed font-medium pl-4 border-l-2 border-primary-400">
                {work.summary}
              </p>

              <div className="mt-7 flex flex-col gap-5">
                {work.content.map((para, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-[1.95]">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">相关标签：</span>
                {work.tags.map((tag) => (
                  <span key={tag} className="text-xs text-secondary-900 bg-secondary-100 px-3 py-1.5 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200 flex flex-col sm:flex-row items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xl">
                    <i className="ri-user-3-line" />
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-bold text-foreground-950">{work.author}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{work.authorNote}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setLiked((v) => !v)}
                    aria-pressed={liked}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer ${
                      liked
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                    </span>
                    {formatNumber(work.likes + (liked ? 1 : 0))}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1.5 bg-background-50 border border-background-300 hover:border-primary-300 text-foreground-700 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={copied ? "ri-check-line text-primary-600" : "ri-link"} />
                    </span>
                    {copied ? "已复制链接" : "分享"}
                  </button>
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 flex flex-col gap-6">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">作品数据</h2>
                  <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-md bg-background-50 py-3">
                      <dt className="text-xs text-foreground-500">点赞</dt>
                      <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                        {formatNumber(work.likes)}
                      </dd>
                    </div>
                    <div className="rounded-md bg-background-50 py-3">
                      <dt className="text-xs text-foreground-500">浏览</dt>
                      <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                        {formatNumber(work.views)}
                      </dd>
                    </div>
                    <div className="rounded-md bg-background-50 py-3">
                      <dt className="text-xs text-foreground-500">评论</dt>
                      <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                        {formatNumber(work.comments)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">相关推荐</h2>
                  <ul className="mt-4 flex flex-col gap-4">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link to={`/work/${item.id}`} className="group flex gap-3 cursor-pointer">
                          <span className="relative w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={item.cover}
                              alt={item.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block font-heading text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">
                              {item.categoryName} · {formatNumber(item.likes)} 点赞
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/works"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    浏览更多参赛作品
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>

                <div className="rounded-lg bg-primary-900 p-5 text-background-100">
                  <h2 className="font-heading text-sm font-bold text-background-50">也想投稿？</h2>
                  <p className="mt-2 text-sm text-primary-100 leading-relaxed">
                    把你的真实观察写下来，通过审核后就会出现在作品集里。
                  </p>
                  <Link
                    to="/about#submit"
                    className="mt-4 inline-flex items-center gap-1.5 bg-accent-500 hover:bg-accent-400 text-primary-950 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-quill-pen-line" />
                    </span>
                    提交参赛作品
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-14 pt-10 border-t border-background-200">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">继续阅读同类作品</h2>
              <Link
                to={`/works?category=${work.category}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                更多{work.categoryName}
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}